"use client";

import { useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { FlashToast } from "@/components/ui-system/flash-toast";
import type { PermissionAction } from "@/lib/api/types/enterprise";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";

type GuardedActionLinkProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> & {
  to: string;
  moduleKey: string;
  action?: PermissionAction;
  deniedMessage?: string;
  replace?: boolean;
  state?: unknown;
  children: ReactNode;
};

export function GuardedActionLink({
  to,
  moduleKey,
  action = "view",
  deniedMessage,
  replace = false,
  state,
  onClick,
  children,
  ...buttonProps
}: GuardedActionLinkProps) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { permissionMap } = useEffectivePermissions();
  const [toast, setToast] = useState<string | null>(null);

  const allowed = hasModulePermission(user, permissionMap, moduleKey, action);
  const message =
    deniedMessage ??
    `You don't have permission to ${action === "view" ? "access this page" : `${action} in this module`}.`;

  return (
    <>
      <button
        type="button"
        {...buttonProps}
        onClick={(event) => {
          onClick?.(event);
          if (event.defaultPrevented) {
            return;
          }
          if (!allowed) {
            setToast(message);
            return;
          }
          navigate(to, { replace, state });
        }}
      >
        {children}
      </button>
      {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}
    </>
  );
}
